import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  Input,
  Textarea,
  Skeleton,
  chakra,
} from '@chakra-ui/react';
import {
  Search,
  Clock,
  PlusCircle,
  ListChecks,
  Check,
  User,
} from 'lucide-react';
import { SingleDatePicker, TimePicker } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import { useUser } from '@/constants/UserContext';
import { insertVolunteerNotification } from '@/utils/volunteerNotificationInsert';
import { ModalShell, TaskListCard, TaskReviewCard, VolunteersTabs } from './components';
import {
  addToTotal,
  hoursToMinutes,
  minutesToHours,
  validateDuration,
} from '@/utils/volunteerHours';
import { toDayKey } from '@/utils/eventShifts';
import { TASK_TITLE_MAX_LENGTH } from '@/pages/VolunteerTasks/volunteerTaskUtils';
import {
  filterVolunteersForAssignment,
  inferActiveDepartmentForEdit,
} from './taskAssignmentUtils';

const ORG_TASK_TABS: { id: string; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'complete', label: 'Complete' },
];

function formatProfileName(
  first?: string | null,
  last?: string | null,
  fallback = 'Unnamed Volunteer',
) {
  const full = `${first?.trim() ?? ''} ${last?.trim() ?? ''}`.trim();
  return full || fallback;
}

type TaskStatus = 'pending' | 'in_progress' | 'completed';

interface TaskRow {
  id: string;
  event_id: string;
  organization_id?: string | null;
  title: string;
  description: string | null;
  assigned_volunteers: string[];
  assigned_departments: string[];
  deadline: string;
  status: TaskStatus;
  created_at: string;
  updated_at?: string | null;
  completed_at: string | null;
  hours_submitted?: boolean;
  assignedVolunteerNames?: string[];
  assignedDepartmentNames?: string[];
}

interface VolunteerRow {
  volunteer_id: string;
  department: string | null;
  fullName: string;
  departmentsList: string[];
}

interface DepartmentInfo {
  name: string;
  count: number;
}

/** A completed task awaiting org review (driven by volunteer_activities). */
interface ReviewItem {
  activityId: string;
  taskId: string;
  taskTitle: string;
  volunteerId: string;
  volunteerName: string;
  startTime: string;
  endTime: string;
  hours: number;
  description: string | null;
}

type NameProfileRow = { id: string; first_name?: string | null; last_name?: string | null };

export interface TasksModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  organizationId: string;
  /** Display name for in-app notifications when volunteers are assigned tasks. */
  eventName?: string;
  /** Org dashboard (create/edit/delete). Volunteers see start/complete only when false. */
  isOrganization: boolean;
  volunteerId?: string;
  /** When opening from a deep link (e.g. task submission notification), show this org sub-tab. */
  initialOrgTab?: 'pending' | 'complete';
  onTasksChanged?: () => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export function TasksModal({
  open,
  onClose,
  eventId,
  organizationId,
  eventName = '',
  isOrganization,
  volunteerId,
  initialOrgTab,
  onTasksChanged,
  onError,
  onSuccess,
}: TasksModalProps) {
  const { user } = useUser();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [volunteers, setVolunteers] = useState<VolunteerRow[]>([]);
  const [departments, setDepartments] = useState<DepartmentInfo[]>([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [activeDepartment, setActiveDepartment] = useState<string | null>(null);
  const [selectedVolunteers, setSelectedVolunteers] = useState<string[]>([]);
  const [deadlineDateStr, setDeadlineDateStr] = useState('');
  const [deadlineTimeStr, setDeadlineTimeStr] = useState('12:00');
  const [editingTask, setEditingTask] = useState<TaskRow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  /** Org-only sub-tab: Pending = live tasks, Complete = completed tasks awaiting review. */
  const [orgTab, setOrgTab] = useState<'pending' | 'complete'>('pending');
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [editedMinutes, setEditedMinutes] = useState<Record<string, number>>({});
  /** Review items with an accept/deny being saved (the ref is the guard, the state the UI). */
  const reviewsInFlightRef = useRef(new Set<string>());
  const [busyReviewIds, setBusyReviewIds] = useState<string[]>([]);
  const prevOpenRef = useRef(false);

  // Callers pass onError inline, so its identity changes on every parent render. Reading it
  // through a ref keeps the fetchers below stable; as a dependency it reloaded the lists on
  // each render, and a failed load (error toast -> parent render) retried forever.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (open && !prevOpenRef.current && isOrganization && initialOrgTab) {
      setOrgTab(initialOrgTab);
      setShowCreateTask(false);
    }
    prevOpenRef.current = open;
  }, [open, isOrganization, initialOrgTab]);

  const initDeadlineDefaults = useCallback(() => {
    const now = new Date();
    // Local day, to match the local time below: the UTC day is still yesterday for the
    // first hours after midnight in any zone ahead of UTC.
    setDeadlineDateStr(toDayKey(now));
    setDeadlineTimeStr(
      `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`,
    );
  }, []);

  const fetchEventDetails = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('name, departments')
        .eq('id', eventId)
        .single();

      if (!error && data) {
        if (Array.isArray(data.departments) && data.departments.length > 0) {
          const cleanedNames = Array.from(
            new Set(
              data.departments.flatMap((dept: unknown) =>
                typeof dept === 'string'
                  ? dept
                      .split(',')
                      .map((part) => part.trim())
                      .filter((part) => part.length > 0)
                  : [],
              ),
            ),
          );
          setDepartments(cleanedNames.map((name) => ({ name, count: 0 })));
        } else {
          setDepartments([]);
        }
      } else {
        setDepartments([]);
      }
    } catch {
      setDepartments([]);
    }
  }, [eventId]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('event_id', eventId)
        .order('deadline', { ascending: true });

      if (error) throw error;

      let enriched: TaskRow[] =
        data?.map((task: Record<string, unknown>) => {
          const base = task as unknown as TaskRow;
          return {
            ...base,
            assigned_volunteers: Array.isArray(task.assigned_volunteers)
              ? (task.assigned_volunteers as string[])
              : [],
            assigned_departments: Array.isArray(task.assigned_departments)
              ? (task.assigned_departments as string[])
              : [],
          };
        }) ?? [];

      if (!isOrganization && volunteerId) {
        let volunteerDepartments: string[] = [];
        const { data: volunteerRecord } = await supabase
          .from('volunteers')
          .select('department')
          .eq('event_id', eventId)
          .eq('volunteer_id', volunteerId)
          .eq('is_active', true)
          .maybeSingle();

        if (volunteerRecord?.department) {
          volunteerDepartments = volunteerRecord.department
            .split(',')
            .map((dept: string) => dept.trim().toLowerCase())
            .filter((dept: string) => dept.length > 0);
        }

        const deptSet = new Set(volunteerDepartments.filter((d) => d.length > 0));
        enriched = enriched.filter((task) => {
          const av = task.assigned_volunteers ?? [];
          const ad = (task.assigned_departments ?? [])
            .map((dept) => (dept ?? '').trim().toLowerCase())
            .filter((dept) => dept.length > 0);
          const isDirect = av.includes(volunteerId);
          const matchesDept =
            ad.length > 0 && [...deptSet].some((d) => ad.includes(d));
          return isDirect || matchesDept;
        });
      }

      const assignedIds = [
        ...new Set(enriched.flatMap((t) => t.assigned_volunteers ?? []).filter(Boolean)),
      ];

      if (assignedIds.length > 0) {
        const { profiles: profileData } = await fetchProfilesByIds<NameProfileRow>(
          assignedIds,
          'id, first_name, last_name',
          'TasksModal.assignedVolunteers',
        );

        const profileMap = new Map(
          profileData.map((p) => [
            p.id,
            formatProfileName(p.first_name, p.last_name, 'Unnamed Volunteer'),
          ]),
        );

        enriched = enriched.map((task) => ({
          ...task,
          assignedVolunteerNames: (task.assigned_volunteers ?? []).map(
            (id) => profileMap.get(id) ?? 'Volunteer',
          ),
          assignedDepartmentNames: (task.assigned_departments ?? [])
            .map((dept) => dept || '')
            .filter(Boolean),
        }));
      } else {
        enriched = enriched.map((task) => ({
          ...task,
          assignedVolunteerNames: [],
          assignedDepartmentNames: (task.assigned_departments ?? [])
            .map((dept) => dept || '')
            .filter(Boolean),
        }));
      }

      enriched.sort((a, b) => {
        if (a.status === b.status) {
          const aDate = a.status === 'completed' ? a.updated_at ?? a.deadline : a.deadline;
          const bDate = b.status === 'completed' ? b.updated_at ?? b.deadline : b.deadline;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        }
        if (a.status === 'pending' || a.status === 'in_progress') return -1;
        return 1;
      });

      setTasks(enriched);
    } catch (e) {
      console.error(e);
      onErrorRef.current?.('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [eventId, isOrganization, volunteerId]);

  const fetchVolunteers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('volunteers')
        .select('volunteer_id, department')
        .eq('event_id', eventId)
        .eq('is_active', true);

      if (error) throw error;

      const volunteerRecords = (data || []).filter(
        (vol) => typeof vol.volunteer_id === 'string' && vol.volunteer_id.length > 0,
      ) as { volunteer_id: string; department: string | null }[];

      const volunteerIds = [...new Set(volunteerRecords.map((v) => v.volunteer_id))];

      let profileMap = new Map<string, { first_name?: string | null; last_name?: string | null }>();
      if (volunteerIds.length > 0) {
        const { profiles: profileData } = await fetchProfilesByIds<NameProfileRow>(
          volunteerIds,
          'id, first_name, last_name',
          'TasksModal.eventVolunteers',
        );
        profileMap = new Map(profileData.map((p) => [p.id, p]));
      }

      const volunteersWithNames: VolunteerRow[] = volunteerRecords.map((vol) => {
        const profile = profileMap.get(vol.volunteer_id);
        const departmentsList =
          typeof vol.department === 'string'
            ? vol.department
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
                .map((d) => d.toLowerCase())
            : [];
        return {
          volunteer_id: vol.volunteer_id,
          department: vol.department,
          fullName: formatProfileName(profile?.first_name, profile?.last_name, 'Unnamed Volunteer'),
          departmentsList,
        };
      });

      setVolunteers(volunteersWithNames);

      const uniqueDepts = volunteerRecords
        .map((v) => v.department)
        .filter((dept): dept is string => typeof dept === 'string' && dept.trim().length > 0)
        .flatMap((dept) =>
          dept
            .split(',')
            .map((p) => p.trim())
            .filter((p) => p.length > 0),
        );

      const counts = uniqueDepts.reduce<Record<string, number>>((acc, dept) => {
        const key = dept.toLowerCase();
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});

      const uniqueNames = [...new Set(uniqueDepts)];

      if (uniqueNames.length > 0) {
        setDepartments((prev) => {
          if (prev.length > 0) {
            const existingNames = new Set(prev.map((d) => d.name.toLowerCase()));
            const updatedPrev = prev.map((d) => ({
              ...d,
              count: counts[d.name.toLowerCase()] ?? 0,
            }));
            const extras = uniqueNames
              .filter((name) => !existingNames.has(name.toLowerCase()))
              .map((name) => ({ name, count: counts[name.toLowerCase()] ?? 0 }));
            return [...updatedPrev, ...extras];
          }
          return uniqueNames.map((name) => ({ name, count: counts[name.toLowerCase()] ?? 0 }));
        });
      } else {
        setDepartments((prev) => prev.map((d) => ({ ...d, count: 0 })));
      }
    } catch (e) {
      console.error(e);
    }
  }, [eventId]);

  const fetchReviewItems = useCallback(async () => {
    setReviewLoading(true);
    try {
      const { data: activities, error } = await supabase
        .from('volunteer_activities')
        .select('id, task_id, volunteer_id, start_time, end_time, hours, description')
        .eq('event_id', eventId)
        .not('task_id', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows = (activities ?? []).filter(
        (a): a is {
          id: string;
          task_id: string;
          volunteer_id: string;
          start_time: string;
          end_time: string;
          hours: number;
          description: string | null;
        } => !!a && typeof a.task_id === 'string' && typeof a.volunteer_id === 'string',
      );

      if (rows.length === 0) {
        setReviewItems([]);
        return;
      }

      const taskIds = [...new Set(rows.map((r) => r.task_id))];
      const { data: taskRows } = await supabase
        .from('tasks')
        .select('id, title, status, hours_submitted')
        .in('id', taskIds);

      const reviewableTasks = new Map<string, { title: string }>();
      for (const t of taskRows ?? []) {
        if (t.status === 'completed' && t.hours_submitted === true) {
          reviewableTasks.set(t.id, { title: t.title });
        }
      }

      const filtered = rows.filter((r) => reviewableTasks.has(r.task_id));
      if (filtered.length === 0) {
        setReviewItems([]);
        return;
      }

      const volunteerIds = [...new Set(filtered.map((r) => r.volunteer_id))];
      const { profiles: profileRows } = await fetchProfilesByIds<NameProfileRow>(
        volunteerIds,
        'id, first_name, last_name',
        'TasksModal.reviewItems',
      );

      const nameById = new Map<string, string>();
      for (const p of profileRows) {
        nameById.set(
          p.id,
          formatProfileName(p.first_name, p.last_name, 'Volunteer'),
        );
      }

      const items: ReviewItem[] = filtered.map((r) => ({
        activityId: r.id,
        taskId: r.task_id,
        taskTitle: reviewableTasks.get(r.task_id)!.title,
        volunteerId: r.volunteer_id,
        volunteerName: nameById.get(r.volunteer_id) ?? 'Volunteer',
        startTime: r.start_time,
        endTime: r.end_time,
        hours: r.hours,
        description: r.description ?? null,
      }));

      setReviewItems(items);
    } catch (e) {
      console.error(e);
      onErrorRef.current?.('Failed to load completed tasks');
      setReviewItems([]);
    } finally {
      setReviewLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!open) return;
    fetchTasks();
    fetchEventDetails();
    if (isOrganization) fetchVolunteers();
  }, [open, eventId, isOrganization, fetchTasks, fetchEventDetails, fetchVolunteers]);

  useEffect(() => {
    if (open && isOrganization && orgTab === 'complete' && !showCreateTask) {
      fetchReviewItems();
    }
  }, [open, isOrganization, orgTab, showCreateTask, fetchReviewItems]);

  useEffect(() => {
    if (open && showCreateTask && isOrganization) {
      fetchVolunteers();
    }
  }, [open, showCreateTask, isOrganization, fetchVolunteers]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // Reset on close only. resetForm is redefined every render and sets fresh arrays, so
    // depending on it would re-run this on every render while closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const volunteersForAssignment = useMemo(
    () => filterVolunteersForAssignment(volunteers, activeDepartment, searchQuery),
    [volunteers, activeDepartment, searchQuery],
  );

  const hasSearchQuery = searchQuery.trim().length > 0;

  /** Org-only "Pending" tab list: everything not yet completed. */
  const pendingTasksForOrg = useMemo(
    () => tasks.filter((t) => t.status !== 'completed'),
    [tasks],
  );

  const buildDeadlineIso = () => {
    const [y, mo, day] = deadlineDateStr.split('-').map(Number);
    const [hh, mm] = deadlineTimeStr.split(':').map(Number);
    if (!y || !mo || !day || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return new Date(y, mo - 1, day, hh, mm, 0, 0).toISOString();
  };

  const handleCreateTask = async () => {
    if (!title.trim()) {
      onError?.('Please enter a task title');
      return;
    }
    if (title.trim().length > TASK_TITLE_MAX_LENGTH) {
      onError?.(`Task title must be ${TASK_TITLE_MAX_LENGTH} characters or fewer`);
      return;
    }
    const combinedDeadline = buildDeadlineIso();
    if (!combinedDeadline) {
      onError?.('Please set a valid deadline date and time');
      return;
    }

    if (selectedVolunteers.length === 0) {
      onError?.('Select at least one volunteer');
      return;
    }

    const taskData = {
      event_id: eventId,
      organization_id: organizationId,
      title: title.trim(),
      description: description.trim() || null,
      assigned_volunteers: selectedVolunteers,
      assigned_departments: [],
      deadline: combinedDeadline,
    };

    setSaving(true);
    try {
      const eventLabel = eventName.trim() || 'the event';
      const taskTitle = title.trim();

      if (editingTask) {
        const { error } = await supabase.from('tasks').update(taskData).eq('id', editingTask.id);
        if (error) throw error;

        const prev = Array.isArray(editingTask.assigned_volunteers)
          ? editingTask.assigned_volunteers
          : [];
        const newlyAssigned = selectedVolunteers.filter((id) => !prev.includes(id));
        for (const vid of newlyAssigned) {
          insertVolunteerNotification({
            user_id: vid,
            title: 'New task assigned',
            message: `You've been assigned a new task in ${eventLabel}: "${taskTitle}".`,
            type: 'task_assigned',
            data: {
              event_id: eventId,
              event_name: eventLabel,
              task_id: editingTask.id,
              task_title: taskTitle,
            },
            read: false,
          });
        }

        onSuccess?.('Task updated');
      } else {
        const { data: createdTask, error } = await supabase
          .from('tasks')
          // Status is set on creation only: an edit must not send a task back to pending.
          .insert({ ...taskData, status: 'pending' as const })
          .select('id')
          .single();
        if (error) throw error;
        const newTaskId = createdTask?.id as string | undefined;
        if (newTaskId) {
          for (const vid of selectedVolunteers) {
            insertVolunteerNotification({
              user_id: vid,
              title: 'New task assigned',
              message: `You've been assigned a new task in ${eventLabel}: "${taskTitle}".`,
              type: 'task_assigned',
              data: {
                event_id: eventId,
                event_name: eventLabel,
                task_id: newTaskId,
                task_title: taskTitle,
              },
              read: false,
            });
          }
        }
        onSuccess?.('Task created');
      }

      resetForm();
      await fetchTasks();
      onTasksChanged?.();
    } catch (e) {
      console.error(e);
      onError?.('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = (taskId: string) => {
    if (!window.confirm('Delete this task?')) return;
    void (async () => {
      try {
        const { error } = await supabase.from('tasks').delete().eq('id', taskId);
        if (error) throw error;
        await fetchTasks();
        onTasksChanged?.();
        onSuccess?.('Task deleted');
      } catch {
        onError?.('Failed to delete task');
      }
    })();
  };

  const handleUpdateTaskStatus = async (task: TaskRow, newStatus: TaskStatus) => {
    try {
      const updates: Record<string, unknown> = { status: newStatus };

      if (!isOrganization && volunteerId) {
        if (newStatus === 'in_progress') {
          const current = Array.isArray(task.assigned_volunteers) ? task.assigned_volunteers : [];
          if (!current.includes(volunteerId)) {
            updates.assigned_volunteers = [...current, volunteerId];
          }
        }
      }

      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      } else if (newStatus === 'pending') {
        updates.completed_at = null;
      }

      const { error } = await supabase.from('tasks').update(updates).eq('id', task.id);
      if (error) throw error;
      await fetchTasks();
      onTasksChanged?.();
    } catch {
      onError?.('Failed to update task status');
    }
  };

  const handleAcceptReview = async (item: ReviewItem, overrideMinutes?: number) => {
    const minutes = overrideMinutes ?? hoursToMinutes(item.hours);
    const durationError = validateDuration(minutes);
    if (durationError) {
      onError?.(durationError);
      return;
    }

    try {
      const { data: currentVol, error: fetchErr } = await supabase
        .from('volunteers')
        .select('hours')
        .eq('volunteer_id', item.volunteerId)
        .eq('event_id', eventId)
        .maybeSingle();

      if (fetchErr) {
        onError?.(fetchErr.message || 'Failed to load volunteer');
        return;
      }
      if (currentVol == null) {
        onError?.('Volunteer not found for this event');
        return;
      }

      const newHours = addToTotal(currentVol.hours, minutes);
      const { error: updateErr } = await supabase
        .from('volunteers')
        .update({ hours: newHours })
        .eq('volunteer_id', item.volunteerId)
        .eq('event_id', eventId);

      if (updateErr) {
        onError?.(updateErr.message || 'Could not add hours');
        return;
      }

      const { error: insertErr } = await supabase.from('accepted_volunteer_hours').insert({
        volunteer_id: item.volunteerId,
        event_id: eventId,
        task_id: item.taskId,
        start_time: item.startTime,
        end_time: item.endTime,
        hours: minutesToHours(minutes),
        description: item.description,
        original_activity_id: item.activityId,
        accepted_by: user?.id ?? organizationId,
      });

      if (insertErr) {
        const { error: rollbackErr } = await supabase
          .from('volunteers')
          .update({ hours: currentVol.hours ?? 0 })
          .eq('volunteer_id', item.volunteerId)
          .eq('event_id', eventId);

        if (rollbackErr) {
          onError?.(rollbackErr.message || 'Failed to restore volunteer hours');
          return;
        }

        onError?.(insertErr.message || 'Failed to record hours');
        return;
      }

      const { error: clearFkErr } = await supabase
        .from('accepted_volunteer_hours')
        .update({ original_activity_id: null })
        .eq('original_activity_id', item.activityId);

      if (clearFkErr) {
        onError?.(clearFkErr.message || 'Failed to finalize accepted hours');
        return;
      }

      const { error: deleteErr } = await supabase
        .from('volunteer_activities')
        .delete()
        .eq('id', item.activityId);

      if (deleteErr) {
        onError?.(deleteErr.message || 'Failed to clear request');
        return;
      }

      // Task stays completed; just clear the review flag so it drops out of the list
      const { error: taskErr } = await supabase
        .from('tasks')
        .update({ hours_submitted: false })
        .eq('id', item.taskId);

      if (taskErr) {
        onError?.(taskErr.message || 'Failed to update task');
        return;
      }

      setReviewItems((prev) => prev.filter((r) => r.activityId !== item.activityId));
      setEditedMinutes((prev) => {
        const { [item.activityId]: _drop, ...rest } = prev;
        return rest;
      });
      onTasksChanged?.();
      onSuccess?.('Hours accepted');
    } catch (e) {
      console.error(e);
      onError?.('Failed to accept hours');
    }
  };

  const handleDenyReview = async (item: ReviewItem, reason: string) => {
    const trimmed = reason.trim();
    try {
      const { error: delErr } = await supabase
        .from('volunteer_activities')
        .delete()
        .eq('id', item.activityId);

      if (delErr) {
        onError?.(delErr.message || 'Failed to deny request');
        return;
      }

      const { error: taskErr } = await supabase
        .from('tasks')
        .update({ status: 'pending', completed_at: null, hours_submitted: false })
        .eq('id', item.taskId);

      if (taskErr) {
        onError?.(taskErr.message || 'Failed to revert task');
        return;
      }

      insertVolunteerNotification({
        user_id: item.volunteerId,
        title: 'Task submission denied',
        message: trimmed
          ? `Your submission for "${item.taskTitle}" was denied: ${trimmed}`
          : `Your submission for "${item.taskTitle}" was denied.`,
        type: 'task_denied',
        data: {
          event_id: eventId,
          task_id: item.taskId,
          task_title: item.taskTitle,
          reason: trimmed || null,
        },
        read: false,
      });

      setReviewItems((prev) => prev.filter((r) => r.activityId !== item.activityId));
      setEditedMinutes((prev) => {
        const { [item.activityId]: _drop, ...rest } = prev;
        return rest;
      });
      onTasksChanged?.();
      onSuccess?.('Submission denied');
    } catch (e) {
      console.error(e);
      onError?.('Failed to deny submission');
    }
  };

  /**
   * Accepting is several requests in a row and the card stays on screen until the last one,
   * so a second click used to run the whole thing again and credit the hours twice.
   */
  const runReviewAction = async (activityId: string, action: () => Promise<void>) => {
    if (reviewsInFlightRef.current.has(activityId)) return;
    reviewsInFlightRef.current.add(activityId);
    setBusyReviewIds((prev) => [...prev, activityId]);
    try {
      await action();
    } finally {
      reviewsInFlightRef.current.delete(activityId);
      setBusyReviewIds((prev) => prev.filter((id) => id !== activityId));
    }
  };

  const handleEditTask = (task: TaskRow) => {
    setEditingTask(task);
    setTitle(task.title);
    setDescription(task.description || '');
    const va = Array.isArray(task.assigned_volunteers) ? task.assigned_volunteers : [];
    const da = Array.isArray(task.assigned_departments) ? task.assigned_departments : [];
    setSelectedVolunteers(va);
    setActiveDepartment(
      inferActiveDepartmentForEdit(
        da,
        va,
        volunteers,
        departments.map((d) => d.name),
      ),
    );
    const taskDeadline = new Date(task.deadline);
    setDeadlineDateStr(toDayKey(taskDeadline));
    setDeadlineTimeStr(
      `${taskDeadline.getHours().toString().padStart(2, '0')}:${taskDeadline.getMinutes().toString().padStart(2, '0')}`,
    );
    setShowCreateTask(true);
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedVolunteers([]);
    setActiveDepartment(null);
    initDeadlineDefaults();
    setShowCreateTask(false);
    setEditingTask(null);
    setSearchQuery('');
  };

  const modalTitle = showCreateTask
    ? editingTask
      ? 'Edit task'
      : 'New task'
    : 'Tasks';

  const tasksModalFooter = showCreateTask ? (
    <Flex gap="3" w="full">
      <Button
        flex="1"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="volyx.borderLight"
        bg="white"
        color="volyx.primary"
        fontWeight="semibold"
        _hover={{ bg: 'volyx.lightGray' }}
        onClick={resetForm}
        disabled={saving}
      >
        Cancel
      </Button>
      <Button
        flex="1"
        borderRadius="xl"
        bg="volyx.action"
        color="white"
        fontWeight="semibold"
        _hover={{ bg: '#0063CC' }}
        onClick={() => void handleCreateTask()}
        disabled={saving}
      >
        {saving ? 'Saving…' : editingTask ? 'Save changes' : 'Create task'}
      </Button>
    </Flex>
  ) : isOrganization ? (
    <Button
      w="full"
      borderRadius="xl"
      bg="volyx.action"
      color="white"
      fontWeight="semibold"
      _hover={{ bg: '#0063CC' }}
      onClick={() => {
        initDeadlineDefaults();
        setShowCreateTask(true);
      }}
    >
      <Flex as="span" align="center" gap="2" justify="center">
        <PlusCircle size={20} />
        Create new task
      </Flex>
    </Button>
  ) : undefined;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxW="min(560px, 96vw)"
      maxH="88vh"
      footer={tasksModalFooter}
    >
      {loading && !showCreateTask ? (
        <Flex direction="column" gap="3">
          {[0, 1, 2].map((k) => (
            <Skeleton key={k} h="100px" borderRadius="xl" />
          ))}
        </Flex>
      ) : showCreateTask ? (
        <Box display="flex" flexDirection="column" gap="5">
          <Box
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="volyx.borderLight"
            bg="white"
            p="4"
            boxShadow="0 1px 4px rgba(0,0,0,0.06)"
          >
            <Text fontSize="sm" fontWeight="bold" color="volyx.primary" m="0" mb="3">
              Task details
            </Text>
            <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" m="0" mb="1.5">
              Title <Text as="span" fontWeight="normal">(max. {TASK_TITLE_MAX_LENGTH})</Text>
            </Text>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task name:"
              maxLength={TASK_TITLE_MAX_LENGTH}
              borderRadius="xl"
              borderColor="volyx.borderLight"
              bg="volyx.lightGray"
              mb="4"
              _focus={{ borderColor: 'volyx.action', bg: 'white', boxShadow: '0 0 0 1px var(--chakra-colors-volyx-action)' }}
            />
            <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" m="0" mb="1.5">
              Description (optional)
            </Text>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What should be done, where to find materials, who to ask…"
              minH="88px"
              borderRadius="xl"
              borderColor="volyx.borderLight"
              bg="volyx.lightGray"
              resize="vertical"
              _focus={{ borderColor: 'volyx.action', bg: 'white', boxShadow: '0 0 0 1px var(--chakra-colors-volyx-action)' }}
            />
          </Box>

          <Box
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="volyx.borderLight"
            bg="white"
            p="4"
            boxShadow="0 1px 4px rgba(0,0,0,0.06)"
          >
            <Flex align="center" gap="2" mb="1">
              <User size={16} color="var(--chakra-colors-volyx-primary)" aria-hidden />
              <Text fontSize="sm" fontWeight="bold" color="volyx.primary" m="0">
                Who is this for?
              </Text>
            </Flex>
            <Text fontSize="xs" color="volyx.textSecondary" m="0" mb="3" lineHeight="1.5">
              Choose a department, then select volunteers.
            </Text>

            <Flex
              gap="2"
              mb="3"
              overflowX="auto"
              overflowY="hidden"
              flexWrap="nowrap"
              pb="1"
              style={{ WebkitOverflowScrolling: 'touch' }}
              css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
            >
              <chakra.button
                type="button"
                flexShrink={0}
                px="3"
                py="1.5"
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
                borderWidth="1px"
                borderColor={activeDepartment === null ? 'volyx.action' : 'volyx.borderLight'}
                bg={activeDepartment === null ? 'volyx.action' : 'white'}
                color={activeDepartment === null ? 'white' : 'volyx.textSecondary'}
                cursor="pointer"
                onClick={() => {
                  setActiveDepartment(null);
                  setSearchQuery('');
                }}
              >
                All
              </chakra.button>
              {departments.map((dept) => {
                const isActive = activeDepartment === dept.name;
                return (
                  <chakra.button
                    key={dept.name}
                    type="button"
                    flexShrink={0}
                    px="3"
                    py="1.5"
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="semibold"
                    borderWidth="1px"
                    borderColor={isActive ? 'volyx.action' : 'volyx.borderLight'}
                    bg={isActive ? 'volyx.action' : 'white'}
                    color={isActive ? 'white' : 'volyx.textSecondary'}
                    cursor="pointer"
                    whiteSpace="nowrap"
                    onClick={() => {
                      setActiveDepartment(dept.name);
                      setSearchQuery('');
                    }}
                  >
                    {dept.name}
                    <Text as="span" ml="1.5" opacity={0.85}>
                      ({dept.count})
                    </Text>
                  </chakra.button>
                );
              })}
            </Flex>

            <Flex
              align="center"
              gap="2"
              px="3"
              py="2.5"
              borderRadius="xl"
              bg="volyx.lightGray"
              borderWidth="1px"
              borderColor="volyx.borderLight"
              mb="3"
            >
              <Search size={16} color="#94A3B8" aria-hidden />
              <Input
                flex="1"
                border="none"
                bg="transparent"
                _focus={{ boxShadow: 'none' }}
                placeholder="Search by name or department…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Flex>

            <Box
              maxH="220px"
              overflowY="auto"
              borderWidth="1px"
              borderColor="volyx.borderLight"
              borderRadius="xl"
              bg="volyx.lightGray"
            >
              {volunteersForAssignment.length > 0 ? (
                volunteersForAssignment.map((vol, idx, arr) => {
                  const sel = selectedVolunteers.includes(vol.volunteer_id);
                  const isLast = idx === arr.length - 1;
                  return (
                    <chakra.button
                      key={vol.volunteer_id}
                      type="button"
                      w="full"
                      textAlign="left"
                      px="3"
                      py="3"
                      borderBottomWidth={isLast ? '0' : '1px'}
                      borderColor="volyx.borderLight"
                      bg={sel ? 'white' : 'transparent'}
                      cursor="pointer"
                      borderLeftWidth="3px"
                      borderLeftColor={sel ? 'volyx.action' : 'transparent'}
                      transition="background 0.15s"
                      _hover={{ bg: sel ? 'white' : 'rgba(255,255,255,0.7)' }}
                      onClick={() =>
                        setSelectedVolunteers((prev) =>
                          prev.includes(vol.volunteer_id)
                            ? prev.filter((id) => id !== vol.volunteer_id)
                            : [...prev, vol.volunteer_id],
                        )
                      }
                    >
                      <Flex justify="space-between" align="center" gap="2">
                        <Box minW="0">
                          <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" m="0" truncate>
                            {vol.fullName}
                          </Text>
                          {vol.department && (
                            <Text fontSize="xs" color="volyx.textSecondary" m="0" truncate>
                              {vol.department}
                            </Text>
                          )}
                        </Box>
                        {sel ? (
                          <Flex
                            align="center"
                            justify="center"
                            w="22px"
                            h="22px"
                            borderRadius="full"
                            bg="volyx.action"
                            flexShrink={0}
                          >
                            <Check size={12} color="white" strokeWidth={3} />
                          </Flex>
                        ) : null}
                      </Flex>
                    </chakra.button>
                  );
                })
              ) : (
                <Text fontSize="sm" color="volyx.textSecondary" p="5" m="0" textAlign="center">
                  {hasSearchQuery
                    ? 'No volunteers match your search.'
                    : activeDepartment
                      ? 'No volunteers in this department.'
                      : 'No volunteers available for this event.'}
                </Text>
              )}
            </Box>
          </Box>

          <Box
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="volyx.borderLight"
            bg="white"
            p="4"
            boxShadow="0 1px 4px rgba(0,0,0,0.06)"
          >
            <Flex align="center" gap="2" mb="1">
              <Clock size={16} color="var(--chakra-colors-volyx-primary)" aria-hidden />
              <Text fontSize="sm" fontWeight="bold" color="volyx.primary" m="0">
                Deadline
              </Text>
            </Flex>
            <Text fontSize="xs" color="volyx.textSecondary" m="0" mb="3" lineHeight="1.5">
              When should this be finished? Volunteers see this on their task list.
            </Text>
            <Flex gap="3" flexWrap="wrap" align="flex-start">
              <Box flex="1" minW={{ base: '100%', sm: '200px' }}>
                <SingleDatePicker
                  value={deadlineDateStr || null}
                  onChange={(d) => setDeadlineDateStr(d ?? '')}
                  placeholder="Date"
                  popover
                  popoverPlacement="top"
                  disabled={saving}
                />
              </Box>
              <Box flex="1" minW={{ base: '100%', sm: '140px' }}>
                <TimePicker
                  id="task-deadline-time"
                  value={deadlineTimeStr}
                  onChange={setDeadlineTimeStr}
                  placeholder="Time"
                  layout="pill"
                  dropdownPlacement="top"
                  disabled={saving}
                />
              </Box>
            </Flex>
          </Box>
        </Box>
      ) : (
        <>
          {isOrganization && (
            <VolunteersTabs
              tabs={ORG_TASK_TABS}
              activeTab={orgTab}
              mb="3"
              ariaLabel="Tasks view"
              onChange={(id) => setOrgTab(id as 'pending' | 'complete')}
            />
          )}

          {isOrganization && orgTab === 'complete' ? (
            reviewLoading ? (
              <Flex direction="column" gap="3">
                {[0, 1, 2].map((k) => (
                  <Skeleton key={k} h="200px" borderRadius="xl" />
                ))}
              </Flex>
            ) : reviewItems.length === 0 ? (
              <Flex direction="column" align="center" py="10" gap="3">
                <ListChecks size={48} color="var(--chakra-colors-volyx-mediumGray)" />
                <Text fontSize="sm" color="volyx.textSecondary" m="0">
                  No completed tasks awaiting review
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="3">
                {reviewItems.map((item) => {
                  const requestedMinutes = hoursToMinutes(item.hours);
                  const edited = editedMinutes[item.activityId];
                  return (
                    <TaskReviewCard
                      key={item.activityId}
                      taskTitle={item.taskTitle}
                      volunteerName={item.volunteerName}
                      description={item.description}
                      startTime={item.startTime}
                      endTime={item.endTime}
                      minutes={edited ?? requestedMinutes}
                      onMinutesChange={(m) =>
                        setEditedMinutes((prev) => ({ ...prev, [item.activityId]: m }))
                      }
                      modified={edited !== undefined && edited !== requestedMinutes}
                      onAccept={() =>
                        void runReviewAction(item.activityId, () => handleAcceptReview(item, edited))
                      }
                      onDeny={(reason) =>
                        void runReviewAction(item.activityId, () => handleDenyReview(item, reason))
                      }
                      busy={busyReviewIds.includes(item.activityId)}
                    />
                  );
                })}
              </Flex>
            )
          ) : pendingTasksForOrg.length === 0 && tasks.length === 0 ? (
            <Flex direction="column" align="center" py="10" gap="3">
              <ListChecks size={48} color="var(--chakra-colors-volyx-mediumGray)" />
              <Text fontSize="sm" color="volyx.textSecondary" m="0">
                No tasks yet
              </Text>
              {isOrganization && (
                <Text fontSize="xs" color="volyx.textSecondary" m="0" textAlign="center">
                  Create your first task to assign work to volunteers.
                </Text>
              )}
            </Flex>
          ) : isOrganization && pendingTasksForOrg.length === 0 ? (
            <Flex direction="column" align="center" py="10" gap="3">
              <ListChecks size={48} color="var(--chakra-colors-volyx-mediumGray)" />
              <Text fontSize="sm" color="volyx.textSecondary" m="0">
                No pending tasks
              </Text>
            </Flex>
          ) : (
            <Flex direction="column" gap="3">
              {(isOrganization ? pendingTasksForOrg : tasks).map((task) => {
                const overdue = Boolean(
                  task.deadline &&
                    new Date(task.deadline) < new Date() &&
                    task.status !== 'completed',
                );
                const volunteerNames =
                  task.assignedVolunteerNames && task.assignedVolunteerNames.length > 0
                    ? task.assignedVolunteerNames
                    : (task.assigned_volunteers ?? []).map(() => 'Volunteer');
                const departmentNames =
                  task.assignedDepartmentNames && task.assignedDepartmentNames.length > 0
                    ? task.assignedDepartmentNames
                    : (task.assigned_departments ?? []).map((d) => d ?? '').filter(Boolean);

                let assignmentDisplay = 'Unassigned';
                if (departmentNames.length > 0) assignmentDisplay = departmentNames.join(', ');
                else if (volunteerNames.length > 0) assignmentDisplay = volunteerNames.join(', ');

                return (
                  <TaskListCard
                    key={task.id}
                    task={task}
                    isOrganization={isOrganization}
                    overdue={overdue}
                    assignmentDisplay={assignmentDisplay}
                    onEdit={() => handleEditTask(task)}
                    onDelete={() => handleDeleteTask(task.id)}
                    onStart={() => void handleUpdateTaskStatus(task, 'in_progress')}
                    onComplete={() => void handleUpdateTaskStatus(task, 'completed')}
                  />
                );
              })}
            </Flex>
          )}
        </>
      )}
    </ModalShell>
  );
}
